import { action, computed, makeObservable, observable } from 'mobx';
import { scrollToElement } from 'telia-front-react';

import EInvoiceSuppliersDataStore from '../data/EInvoiceSuppliersDataStore';
import InvoiceSettingsChangeLogStore from '../InvoiceSettingsChangeLogStore';
import InvoiceSettingsModalStore from '../InvoiceSettingsModalStore';
import InvoiceSettingsPanelStore from '../InvoiceSettingsPanelStore';
import { InvoiceSettings } from '../models/CustomerInvoicesSettings';
import { ITranslateStore } from '../TranslateStore';
import { ITemplateBillingSettingsCardDeliveryChannel } from '../../templates/settings-card';

export default class InvoiceSettingsViewStore {
  @observable
  private isOpen = false;

  constructor(
    private translateStore: ITranslateStore,
    private invoiceSettingsModalStore: InvoiceSettingsModalStore,
    private eInvoiceSuppliersDataStore: EInvoiceSuppliersDataStore,
    private invoiceSettingsChangeLogStore: InvoiceSettingsChangeLogStore,
    private invoiceSettingsPanelStore: InvoiceSettingsPanelStore
  ) {
    makeObservable(this);
  }

  @action
  public setCardOpen(open: boolean, forceScroll?: boolean): void {
    const wasOpen = this.isOpen;
    this.isOpen = open;

    if ((this.isOpen && !wasOpen) || forceScroll) {
      scrollToElement('invoice-settings-card');
    }
  }

  @computed
  public get isCardOpen(): boolean {
    return this.isOpen;
  }

  public toDeliveryChannels(invoiceSettings: InvoiceSettings): ITemplateBillingSettingsCardDeliveryChannel[] {
    const translate = this.translateStore.translate;

    if (invoiceSettings.state === 'UPDATING') {
      return [
        {
          title: this.translateStore.translate('invoices.invoice-settings.settings-button.saving'),
          canTruncateTitle: true
        }
      ];
    }

    const deliveryChannels = [];

    if (invoiceSettings.invoiceDeliveries.includes('INVOICE_CENTRAL')) {
      deliveryChannels.push({
        title: translate('invoices.invoice-settings.delivery-channels.invoice-central'),
        description: invoiceSettings.invoiceCentralSupplier
      });
    }
    if (invoiceSettings.invoiceDeliveries.includes('BANK')) {
      let description;
      if (this.getMultipleDeliveryOptionsSupported(invoiceSettings) && this.eInvoiceSuppliersDataStore.eInvoiceSuppliers.isFinished) {
        description = this.eInvoiceSuppliersDataStore.banks.find((bank) => bank.channelCode === invoiceSettings.bankCode).name;
      } else {
        description = invoiceSettings.ibanOrUserCode;
      }
      deliveryChannels.push({
        title: translate('invoices.invoice-settings.delivery-channels.bank'),
        description: description
      });
    }
    if (invoiceSettings.invoiceDeliveries.includes('MOBILE')) {
      deliveryChannels.push({
        title: translate('invoices.invoice-settings.delivery-channels.mobile'),
        description: invoiceSettings.mobileNumber
      });
    }
    if (invoiceSettings.invoiceDeliveries.includes('EMAIL')) {
      let addTitle = true;
      for (const emailAddress of invoiceSettings.emailAddresses) {
        deliveryChannels.push({
          title: addTitle ? translate('invoices.invoice-settings.delivery-channels.email') : null,
          canTruncateTitle: true,
          description: emailAddress
        });
        addTitle = false;
      }
    }
    if (invoiceSettings.invoiceDeliveries.includes('POST')) {
      deliveryChannels.push({
        title: translate('invoices.invoice-settings.delivery-channels.post'),
        description: invoiceSettings.address.fullAddress
      });
    }
    if (deliveryChannels.length === 0) {
      return [
        {
          title: translate('invoices.invoice-settings.delivery-channels.self-service')
        }
      ];
    } else {
      return deliveryChannels;
    }
  }

  @action
  public async activateInvoiceSettings(referenceNumber: string): Promise<any> {
    await this.invoiceSettingsModalStore.openModal(referenceNumber);
    const settings = this.invoiceSettingsPanelStore.invoiceSettings;
    if (this.getMultipleDeliveryOptionsSupported(settings)) {
      this.invoiceSettingsChangeLogStore.loadChangeLog(referenceNumber, settings.source).catch((e) => console.error(e));
    }
    this.setCardOpen(true);
  }

  @action
  public async activateInvoiceSettingsAndLoadEInvoiceSuppliers(settings: InvoiceSettings): Promise<any> {
    if (this.getMultipleDeliveryOptionsSupported(settings)) {
      this.invoiceSettingsChangeLogStore.loadChangeLog(settings.refNumber, settings.source).catch((e) => console.error(e));
    }
    await this.invoiceSettingsModalStore.openModal(settings.refNumber);
    this.setCardOpen(true);
  }

  public getMultipleDeliveryOptionsSupported(settings: InvoiceSettings): boolean {
    return settings.source === 'BILL';
  }
}
